import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import './TGillRebuildPlan.css';

const normalize = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const number = value => value == null ? '—' : value;
const dateTime = value => value ? new Date(value).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'unavailable';

function PlayerCard({ player, label }) {
  return <article className="rr-player">
    <div className="rr-player-head"><span className={`rr-pos rr-pos--${player.position?.toLowerCase()}`}>{player.position}</span><div><h3>{player.name}</h3><small>{player.team} · {player.age ? `Age ${player.age}` : 'Age unavailable'}</small></div><b>{player.dynasty_rank < 9999 ? `#${player.dynasty_rank}` : 'NR'}</b></div>
    {label && <span className="rr-label">{label}</span>}
    <p>{player.why}</p>
    <div className="rr-facts">{(player.facts || []).map(fact => <span key={fact}>{fact}</span>)}</div>
  </article>;
}

function RosterBucket({ title, subtitle, rows, tone }) {
  return <section className={`rr-panel rr-bucket rr-bucket--${tone}`}>
    <header><div><span>ROSTER DECISION</span><h2>{title}</h2><p>{subtitle}</p></div><b>{rows?.length || 0}</b></header>
    <div className="rr-player-grid">{(rows || []).map(player => <PlayerCard key={player.player_id} player={player} />)}</div>
    {!rows?.length && <p className="rr-empty">No player currently clears this evidence filter.</p>}
  </section>;
}

function PickCard({ pick }) {
  return <article className="rr-pick"><div><small>{pick.year} ROUND {pick.round}</small><strong>{pick.slot_label}</strong></div><div><b>{pick.market_value} pts</b><span>{pick.market_label} · {pick.confidence} confidence</span></div><p>{pick.explanation}</p></article>;
}

export default function TGillRebuildPlan({ api }) {
  const { currentUser } = useUser();
  const allowed = !currentUser?.guest && [currentUser?.display_name, currentUser?.username].some(value => normalize(value) === 'tgill55');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async signal => {
    if (!allowed) return;
    setBusy(true);
    try {
      const response = await fetch(`${api}/api/dynasty/rebuild-plan`, { credentials: 'include', signal });
      if (response.status === 401 || response.status === 403) throw new Error('This private rebuild room is locked to TGill55’s authenticated Dynasty account.');
      if (!response.ok) throw new Error('The weekly plan could not be reconciled. Try again shortly.');
      const payload = await response.json();
      if (!signal?.aborted) { setData(payload); setError(''); }
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message);
    } finally {
      if (!signal?.aborted) setBusy(false);
    }
  }, [allowed, api]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    const timer = setInterval(() => load(controller.signal), 300000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [load]);

  if (!allowed) return <div className="empty-state"><p>This private rebuild room belongs to TGill55.</p></div>;

  const score = data?.scorecard || {};
  const freshnessWarning = data && (!data.freshness?.projection?.available || !data.freshness?.recent_role?.available);
  return <div className="rr-page">
    <section className="rr-hero">
      <div><span className="rr-kicker">PRIVATE · TGILL55 · {data?.edition || 'WEEKLY EDITION'}</span><h1>Rebuild Room</h1><p>{data?.phase_summary || 'Building this week’s evidence-backed roster plan…'}</p></div>
      <div className="rr-hero-actions">{data && <div className="rr-phase"><small>CURRENT PHASE</small><strong>{data.phase}</strong></div>}<button className="btn btn-refresh" disabled={busy} onClick={() => load()}>{busy ? 'Reconciling…' : '↻ Rebuild plan'}</button></div>
    </section>
    {error && <div className="rr-banner rr-banner--warn" role="alert">{error}</div>}
    {!data && !error && <section className="rr-panel"><p>Auditing roster age, value, role and draft capital…</p></section>}
    {data && <>
      {freshnessWarning && <div className="rr-banner rr-banner--warn">One or more live feeds are unavailable. Missing current-week or completed-game data is shown as unavailable—not treated as zero.</div>}
      <section className="rr-scoreboard" aria-label="Rebuild scorecard">
        <div><small>POWER</small><b>{score.power_rank ? `#${score.power_rank}` : '—'}</b><span>current league rank</span></div>
        <div><small>ROSTER</small><b>{number(score.roster_grade)}</b><span>complete-roster grade</span></div>
        <div><small>YOUTH</small><b>{number(score.youth_rate)}%</b><span>inside position curve</span></div>
        <div><small>CORE</small><b>{number(score.cornerstones)}</b><span>protected assets</span></div>
        <div><small>1STS</small><b>{number(score.future_firsts)}</b><span>next two drafts</span></div>
        <div><small>PICK CHEST</small><b>{score.pick_portfolio_rank ? `#${score.pick_portfolio_rank}` : '—'}</b><span>league portfolio rank</span></div>
      </section>

      <section className="rr-panel rr-actions">
        <header><div><span>WEEKLY OPERATING PLAN</span><h2>Do these in order</h2><p>Every step has a reason and a finish line.</p></div><b>{data.weekly_actions.length} steps</b></header>
        <div className="rr-timeline">{data.weekly_actions.map(item => <article key={item.rank}>
          <b className="rr-step">{item.rank}</b><div><small>{item.window} · {item.title}</small><h3>{item.action}</h3><p>{item.why}</p><footer><strong>DONE WHEN</strong> {item.success}</footer></div>
        </article>)}</div>
      </section>

      <div className="rr-buckets">
        <RosterBucket title="Protect" subtitle="The young value foundation. Require a tier-up or premium overpay." rows={data.roster_plan.cornerstones} tone="protect" />
        <RosterBucket title="Develop" subtitle="Give these players another evidence window. Do not sell low." rows={data.roster_plan.develop} tone="develop" />
        <RosterBucket title="Shop now" subtitle="Create a market while current production still helps contenders." rows={data.roster_plan.shop} tone="shop" />
        <RosterBucket title="Churn" subtitle="Roster spots to improve when a stronger youth/role signal appears." rows={data.roster_plan.replace} tone="replace" />
      </div>

      <section className="rr-panel">
        <header><div><span>WAIVER LAB</span><h2>Young role signals the league has not rostered</h2><p>Availability is inferred from the live league roster pool; verify the claim screen before acting.</p></div></header>
        <div className="rr-player-grid">{data.waiver_stashes.map(player => <PlayerCard key={player.player_id} player={player} label={`Evidence ${player.evidence_score}`} />)}</div>
        {!data.waiver_stashes.length && <p className="rr-empty">No unrostered youth currently clears the role/projection filter. Preserve the roster spot.</p>}
      </section>

      <section className="rr-panel">
        <header><div><span>LEAGUE ACQUISITION BOARD</span><h2>Young assets worth monitoring</h2><p>This is a watchlist, not permission to overpay. Run every package through the evaluator.</p></div><Link to="/trades">Open evaluator →</Link></header>
        <div className="rr-player-grid">{data.league_targets.map(player => <PlayerCard key={player.player_id} player={player} label={`Rostered by ${player.manager}`} />)}</div>
      </section>

      <section className="rr-panel rr-capital">
        <header><div><span>DRAFT CAPITAL</span><h2>{data.draft_capital.picks_held_count} picks · {data.draft_capital.total_value} market points</h2><p>{data.draft_capital.methodology}</p></div><Link to="/pick-exchange">Open Pick Exchange →</Link></header>
        <div className="rr-pick-grid">{data.draft_capital.picks.map(pick => <PickCard key={pick.pick_id} pick={pick} />)}</div>
        {!!data.draft_capital.own_picks_outgoing.length && <div className="rr-banner rr-banner--danger"><strong>Own-pick exposure:</strong> {data.draft_capital.own_picks_outgoing.map(pick => `${pick.year} ${pick.slot_label} now held by ${pick.current_owner_display_name}`).join(' · ')}</div>}
      </section>

      <section className="rr-panel rr-guardrails">
        <header><div><span>REBUILD DISCIPLINE</span><h2>Rules that prevent expensive shortcuts</h2></div></header>
        <ol>{data.guardrails.map(rule => <li key={rule}>{rule}</li>)}</ol>
      </section>

      <footer className="rr-method"><p>{data.methodology}</p><span>Generated {dateTime(data.generated_at)} · Projections {dateTime(data.freshness?.projection?.fetched_at)} · Role sample {data.freshness?.recent_role?.weeks?.length ? `Weeks ${data.freshness.recent_role.weeks.join(', ')}` : 'unavailable'} · Auto-reconciles every 5 minutes</span></footer>
    </>}
  </div>;
}
